/* eslint-disable react/prop-types */
import React, { useContext, useMemo, useState } from 'react';
import { Box, Button, Grid, Stack, TextField, Typography, IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import UIContext from '../../context/UIContext';
import axios from '../../utils/axios';
import successIcon from '../../media/success_icon.png';

const loginInitialValue = {
  password: '',
  confirmPassword: '',
};

const ChangePassword = ({ setDialogOpen }) => {
  const [verificationStage, setVerificationStage] = useState(false);
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);
  const navigate = useNavigate();
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [confirmPasswordVisibility, setConfirmPasswordVisibility] = useState(false);
  const togglePasswordVisibility = () => setPasswordVisibility((prev) => !prev);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisibility((prev) => !prev);

  const ValidationSchema = useMemo(
    () =>
      Yup.object({
        password: Yup.string()
          .min(6, 'Password must be at least 6 characters long')
          .max(15, 'Password cannot exceed 15 characters')
          .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
          .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
          .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
          .required('Password is required'),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password')], 'Passwords must match')
          .required('Please confirm your password'),
      }),
    []
  );

  const handlePasswordChange = async (Password) => {
    try {
      showLoading();
      const response = await axios.post('profile/editprofile/ChangePassword', {
        EmailId: sessionStorage.getItem('UserEmail'),
        Password,
        UserID: sessionStorage.getItem('UserId'),
      });
      if (response.status === 200) {
        setVerificationStage(true);
      }
    } catch (e) {
      openSnackBar(e.response?.data || 'Something went wrong. Please try again!', 'error');
    }
    hideLoading();
  };

  const handleLogout = () => {
    navigate('/');
    sessionStorage.clear();
  };

  if (verificationStage) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        sx={{ padding: 4, height: '100%' }}
      >
        <img width={100} src={successIcon} alt="Success Icon" style={{ marginBottom: '16px' }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '8px' }} color="success.main">
          Password Changed Successfully!
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: '24px' }}>
          Your password has been updated. Please log in again using your new password for enhanced security.
        </Typography>
        <Button
          onClick={handleLogout}
          variant="contained"
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            textTransform: 'none',
          }}
          endIcon={<LogoutIcon />}
        >
          Logout and Login Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Change Password
      </Typography>
      <Formik
        validationSchema={ValidationSchema}
        initialValues={loginInitialValue}
        onSubmit={(values) => handlePasswordChange(values.password)}
      >
        {({ values, errors, handleBlur, handleChange, touched }) => (
          <Form>
            <Stack spacing={2}>
              <TextField
                size="small"
                fullWidth
                name="password"
                label="New Password"
                type={passwordVisibility ? 'text' : 'password'}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && !!errors.password}
                helperText={touched.password && errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility} edge="end">
                        {passwordVisibility ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={confirmPasswordVisibility ? 'text' : 'password'}
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirmPassword && !!errors.confirmPassword}
                helperText={touched.confirmPassword && errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleConfirmPasswordVisibility} edge="end">
                        {confirmPasswordVisibility ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" spacing={2} justifyContent={'flex-end'}>
                <Button onClick={() => setDialogOpen(false)} color="warning" variant="contained">
                  Close
                </Button>
                <Button variant="contained" color="info" type="submit" disabled={Object.keys(errors).length > 0}>
                  Change Password
                </Button>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ChangePassword;
